import { PLATFORM_CONFIG, ProjectTheme, THEME_PLATFORM_CONFIG } from 'platform-config'
import { camelCaseToSnakeCase, deepClone, hexToRgb, omit, pick, transformHexToRgba } from 'utils'
import { ref } from 'vue'
import { APP_CONFIG } from '../../../crypto-otc/src/project-config'
import { StorageIndexDBEnum } from '../storage/StorageDB'

export const useProjectTheme = defineStore('projectTheme', () => {
  const themeKey = `${import.meta.env.VITE_PLATFORM}__${StorageIndexDBEnum.ThemeType}`

  const fetchTheme = computed<'light' | 'dark'>(() => {
    if (!APP_CONFIG.isShowThemeSwitch) return PLATFORM_CONFIG.themeType as ProjectTheme

    const cache = localStorage.getItem(themeKey) as ProjectTheme
    return cache ?? (PLATFORM_CONFIG.themeType as ProjectTheme)
  })

  const themeType = ref<'light' | 'dark'>(fetchTheme.value)

  const toggleTheme = (newTheme?: ProjectTheme) => {
    themeType.value = newTheme ? newTheme : themeType.value === ProjectTheme.Light ? ProjectTheme.Dark : ProjectTheme.Light
    window.localStorage.setItem(themeKey, themeType.value)
  }

  const theme = computed(() => {
    return deepClone(THEME_PLATFORM_CONFIG[themeType.value])
  })

  watch(
    () => theme.value,
    () => {
      document.body.style.backgroundColor = 'var(--colors-bg-base)'
    },
    {
      immediate: true,
      deep: true,
    },
  )

  const setCssVariables = () => {
    const themeData = theme.value
    // 循环colors对象，如果key是字符串，则直接加--组成css变量，如果是对象，就继续循环对象， 需要考虑多级，所以需要递归， 然后和最外层的key拼接成css变量
    const cssVariables: Record<string, string> = {
      ...Object.fromEntries(Array.from({ length: 30 }, (_, i) => [`--spacing-${i + 1}`, theme.value.spacing(i + 1)])),
      '--1a-colors-bg-main-page-card': transformHexToRgba(theme.value.colors.bg.mainPage.card, 0.1),
      '--00-colors-bg-main-page-card': transformHexToRgba(theme.value.colors.bg.mainPage.card, 0),
      '--00-colors-bg-base': transformHexToRgba(theme.value.colors.bg.base, 0),
      '--header-height': `${PLATFORM_CONFIG.headerHeight}px`,
      '--content-width': theme.value.contentWidth,
      '--debit-card-card-radius': theme.value.debitCard?.cardRadius || '',
      '--el-skeleton-to-color-50': transformHexToRgba(theme.value.colors.bg.mainPage.card, 0.5),
      '--van-tabs-nav-background': theme.value.colors.bg.base,
      '--van-tab-active-text-color': theme.value.colors.text.base,
      '--van-tab-text-color': theme.value.colors.text.secondary,
      '--el-fill-color-light': theme.value.colors.bg.tag.base,
    }
    const join = (obj: Record<string, any>, prefix: string) => {
      Object.entries(obj).forEach(([key, value]) => {
        key = camelCaseToSnakeCase(key).replace(/_/g, '-')
        if (typeof value === 'string') {
          let joinStr = prefix
          if (prefix === '--' || prefix.endsWith('-')) {
            joinStr = prefix
          } else if (prefix.endsWith('-')) {
            joinStr = prefix
          } else {
            joinStr = `${prefix}-`
          }
          const varName = `${joinStr}${key}`
          cssVariables[varName] = value

          // 如果是颜色变量（hex 格式），同时生成 RGB 版本
          if (prefix.includes('colors') && typeof value === 'string' && value.startsWith('#')) {
            cssVariables[`${varName}-rgb`] = hexToRgb(value)
          }
        } else {
          let joinStr = prefix
          if (joinStr === '--') {
            joinStr = prefix
          } else {
            joinStr = `${prefix}-`
          }
          join(value, `${joinStr}${key}`)
        }
      })
    }

    // 注入 header 配置为 CSS 变量，便于在样式中通过 var(--header-*) 引用
    const cloneThemeData = deepClone(themeData)
    if (cloneThemeData.header) {
      cloneThemeData.header = omit(cloneThemeData.header, ['userIcon'])
    }
    join(pick(cloneThemeData, 'colors', 'font', 'header', 'cloud'), '--')

    if (import.meta.env.DEV) {
      console.log('cssVariables', cssVariables)
    }

    const el = document.documentElement
    Object.entries(cssVariables).forEach(([property, value]) => {
      if (value) {
        el.style.setProperty(property, value)
      }
    })
    return cssVariables
  }

  const setDarkCssVar = (isClear = false) => {
    const el = document.documentElement
    const colors = theme.value.colors
    const baseColor = colors.bg.base

    if (el) {
      const cssVariables = {
        '--el-bg-color-overlay': colors.bg.mainPage.card,
        '--el-border-color-lighter': colors.cardBorder,
        '--el-border-color': colors.cardBorder,
        '--el-bg-color': colors.bg.mainPage.card,
        '--el-color-primary': colors.primary,
        '--color-text-base': colors.text.base,
        '--van-picker-background': colors.bg.base,
        '--van-picker-mask-color': `linear-gradient(180deg, ${transformHexToRgba(baseColor, 0.9)}, ${transformHexToRgba(baseColor, 0.4)}), linear-gradient(0deg, ${transformHexToRgba(baseColor, 0.9)}, ${transformHexToRgba(baseColor, 0.4)})`,
        '--van-picker-option-text-color': colors.text.base,
        '--van-border-color': colors.cardBorder,
      }

      Object.entries(cssVariables).forEach(([property, value]) => {
        if (isClear) {
          el.style.removeProperty(property)
        } else {
          el.style.setProperty(property, value)
        }
      })
    }
  }

  const setLightCssVar = () => {
    const colors = theme.value.colors
    const el = document.documentElement
    if (el) {
      el.style.setProperty('--color-up', colors.up)
      el.style.setProperty('--color-down', colors.down)
    }
  }

  watch(
    () => themeType.value,
    (type, oldType) => {
      if (type !== oldType) {
        if (themeType.value === ProjectTheme.Light) {
          document.documentElement.classList.remove('dark')
          document.documentElement.classList.add('light')
          setDarkCssVar(true)
        } else {
          document.documentElement.classList.remove('light')
          document.documentElement.classList.add('dark')
          setDarkCssVar()
        }
        setLightCssVar()

        setCssVariables()
      }
    },
    {
      immediate: true,
    },
  )

  return {
    themeType,
    toggleTheme,
    theme,
  }
})
